import { fetchWithRetry as fetch, responseError } from './apiError';
import { tokenService } from './tokenService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface DesignShareInfo {
  id: string;
  project_id: string;
  share_id: string;
  is_public: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface SharedDesignResponse {
  project_id: string;
  share_id: string;
  is_public: boolean;
  design_pages: {
    id: string;
    page_name: string;
    jsx_content: string;
    compiled_html?: string;
    version: number;
    updated_at: string | null;
  }[];
}

function authHeaders(): Record<string, string> {
  const token = tokenService.getAccessToken();

  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function createOrGetShare(projectId: string): Promise<DesignShareInfo> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/projects/${projectId}/design-share`,
    { method: 'POST', headers: authHeaders() },
  );

  if (!response.ok) throw await responseError(response);

  return response.json();
}

async function getShare(projectId: string): Promise<DesignShareInfo | null> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/projects/${projectId}/design-share`,
    { headers: authHeaders() },
  );

  if (response.status === 404) return null;
  if (!response.ok) throw await responseError(response);

  return response.json();
}

async function toggleVisibility(projectId: string, isPublic: boolean): Promise<DesignShareInfo> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/projects/${projectId}/design-share`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ is_public: isPublic }),
    },
  );

  if (!response.ok) throw await responseError(response);

  return response.json();
}

async function deleteShare(projectId: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/projects/${projectId}/design-share`,
    { method: 'DELETE', headers: authHeaders() },
  );

  if (!response.ok) throw await responseError(response);
}

async function getPublicDesign(shareId: string): Promise<SharedDesignResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/shared/design/${shareId}`,
    { headers: { 'Content-Type': 'application/json' } },
  );

  if (response.status === 403) {
    throw Object.assign(new Error('Private'), { status: 403 });
  }

  if (!response.ok) throw await responseError(response);

  return response.json();
}

async function getPrivateDesign(shareId: string): Promise<SharedDesignResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/shared/design/${shareId}/auth`,
    { headers: authHeaders() },
  );

  if (!response.ok) throw await responseError(response);

  return response.json();
}

export const designShareService = {
  createOrGetShare,
  getShare,
  toggleVisibility,
  deleteShare,
  getPublicDesign,
  getPrivateDesign,
};
